import axios from 'axios'

import type { components, operations } from '../constants/schema'

import { toast } from '../utils'
import { abrSetUser } from '../utils/abr'
import { chUpdateUser } from '../utils/ch'

type GetCashTransactionParams = Required<operations['UserController_getUserCashTransactionLog']['parameters']>['query']
type GetUserProductSoldListParams = Required<
  operations['UserController_getCurrentUserProductSold']['parameters']
>['query']

/**
 * 로그인 된 유저 정보 불러오기
 * @returns UnmaskedUserDto
 */
export const getUserInfo = async () => {
  try {
    const userInfo = (await axios.get<components['schemas']['UnmaskedUserDto']>('user/current/unmask')).data

    abrSetUser(userInfo)
    chUpdateUser(userInfo)

    return userInfo
  } catch (error) {
    console.log({ error })
  }
}

/**
 * 로그인 유저 정보 수정
 * @param UserUpdateDto
 * @returns UserDto
 */
export const updateUserInfo = async (data: components['schemas']['UserUpdateDto']) => {
  try {
    const userInfo = (await axios.put<components['schemas']['UserDto']>('user/current', data)).data

    abrSetUser(userInfo)
    chUpdateUser(userInfo)

    return userInfo
  } catch (error) {
    throw error
  }
}

/**
 * 로그인 유저 투자내역 정보
 * @returns ResUserAssetSummary
 */
export const getAssetSummary = async () => {
  return (await axios.get<components['schemas']['ResUserAssetSummary']>('/user/current/assetSummary')).data
}

/**
 * 작품 정산하기
 * @param userProductId 유저 작품 id
 * @param rrn 주민등록번호
 * @return UserProductSettleInformationDto
 */
export const settlement = async ({
  userProductId,
  data,
}: {
  userProductId: number
  data: components['schemas']['ReqQuerySettleUserProduct']
}) => {
  try {
    await axios.post<components['schemas']['UserProductSettlementDto']>(`/user/product/${userProductId}/settle`, data)
  } catch (error) {
    throw error
  }
}

/**
 * 로그인 유저 매각 완료 작품 목록 조회
 * @returns
 */
export const getUserProductSoldList = async ({ size = 50, offset = 0, sort }: GetUserProductSoldListParams) => {
  return (
    await axios.get<components['schemas']['ResListUserProduct']>('/user/current/product/sold', {
      params: { size, offset, sort },
    })
  ).data
}

/**
 * 로그인 유저 거래내역 조회
 * @param size call 한 번에 불러오는 개수
 * @param offset pagination
 * @param transactionType 거래 구분
 * @param sort 정렬 방법
 * @returns ResListCashTransactionLog
 */
export const getCashTransaction = async ({
  size = 50,
  offset = 0,
  transactionType,
  sort,
}: GetCashTransactionParams) => {
  return (
    await axios.get<components['schemas']['ResListCashTransactionLog']>('/user/current/cash/transaction', {
      params: { size, offset, transactionType, sort },
    })
  ).data
}

/**
 * 투자적합성 검사 목록
 * @returns SuitabilityListDto
 */
export const getSuitability = async () => {
  return (await axios.get<components['schemas']['SuitabilityListDto']>('/suitability')).data
}

/**
 * 투자적합성 검사 제출
 * @param data SuitabilityPaperDto
 * @returns SuitabilityResultListDto
 */
export const submitSuitability = async (data: components['schemas']['SuitabilityPaperDto']) => {
  try {
    return (await axios.post<components['schemas']['SuitabilityResultListDto']>('/suitability/paper', data)).data
  } catch (error) {
    throw error
  }
}

/**
 * 로그인 유저 탈퇴 전 확인 정보
 * @returns ResUserAssetSummaryForWithdraw
 */
export const getWithdrawalUser = async () => {
  return (
    await axios.get<components['schemas']['ResUserAssetSummaryForWithdraw']>('/user/current/assetSummary/withdraw')
  ).data
}

/**
 * 회원 탈퇴
 */
export const withdrawalUser = async () => {
  try {
    await axios.delete('/user/current')
  } catch (error) {
    if (axios.isAxiosError(error)) {
      toast(error.response?.data.message, error)
    }
  }
}

export type { GetCashTransactionParams, GetUserProductSoldListParams }
